import { Menu as BaseMenu } from '@base-ui/react/menu'
import { MenuTrigger } from './nested/trigger'
import { MenuContent } from './nested/content'
import { MenuPortal } from './nested/portal'
import { MenuBackdrop } from './nested/backdrop'
import { MenuPositioner } from './nested/positioner'
import { MenuPopup } from './nested/popup'
import { MenuItem } from './nested/item'
import { MenuStaticItem } from './nested/static-item'
import { MenuLinkItem } from './nested/link-item'
import { MenuSeparator } from './nested/separator'
import { MenuGroup } from './nested/group'
import { MenuGroupLabel } from './nested/group-label'
import { MenuCheckboxItem } from './nested/checkbox-item'
import { MenuCheckboxItemIndicator } from './nested/checkbox-item-indicator'
import { MenuRadioGroup } from './nested/radio-group'
import { MenuRadioItem } from './nested/radio-item'
import { MenuRadioItemIndicator } from './nested/radio-item-indicator'
import { MenuSubmenuRoot } from './nested/submenu-root'
import { MenuSubmenuTrigger } from './nested/submenu-trigger'
import { ListItemText } from '../../lib/list-item-text'
import { ListItemLead } from '../../lib/list-item-lead'
import type { PartProps } from '../../lib/part-props'

/**
 * Root props interface for Menu component.
 * Extends base-ui's Menu.Root.Props to maintain compatibility with all base-ui features.
 */
interface MenuRootProps extends PartProps<BaseMenu.Root.Props> {}

/**
 * Menu - A dropdown menu component with keyboard navigation support.
 *
 * This component wraps base-ui's Menu.Root and provides a consistent API
 * with the rest of the Highlight UI component library.
 *
 * @example
 * ```tsx
 * <Menu>
 *   <Menu.Trigger>Open Menu</Menu.Trigger>
 *   <Menu.Portal>
 *     <Menu.Positioner>
 *       <Menu.Popup>
 *         <Menu.Arrow />
 *         <Menu.Item>Item 1</Menu.Item>
 *         <Menu.Item>Item 2</Menu.Item>
 *       </Menu.Popup>
 *     </Menu.Positioner>
 *   </Menu.Portal>
 * </Menu>
 * ```
 */
function Menu({ children, ...props }: MenuRootProps) {
  return <BaseMenu.Root {...props}>{children}</BaseMenu.Root>
}

Menu.Trigger = MenuTrigger
Menu.Content = MenuContent
Menu.Portal = MenuPortal
Menu.Backdrop = MenuBackdrop
Menu.Positioner = MenuPositioner
Menu.Popup = MenuPopup
Menu.Item = MenuItem
Menu.LinkItem = MenuLinkItem
Menu.Static = MenuStaticItem
Menu.Separator = MenuSeparator
Menu.Group = MenuGroup
Menu.GroupLabel = MenuGroupLabel
Menu.CheckboxItem = MenuCheckboxItem
Menu.CheckboxItemIndicator = MenuCheckboxItemIndicator
Menu.RadioGroup = MenuRadioGroup
Menu.RadioItem = MenuRadioItem
Menu.RadioItemIndicator = MenuRadioItemIndicator
Menu.SubmenuRoot = MenuSubmenuRoot
Menu.SubmenuTrigger = MenuSubmenuTrigger
Menu.ItemText = ListItemText
Menu.ItemLead = ListItemLead

export { Menu }
